import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TimelineItem } from '@/types/item';
import DependencyPicker, { DependencyMilestone, DependencyRef } from './DependencyPicker';
import { toast } from 'sonner';

interface DependencyDialogProps {
  open: boolean;
  onClose: () => void;
  sourceKind: 'item' | 'milestone';
  sourceId: string;
  sourceName: string;
  availableItems: TimelineItem[];
  milestones: DependencyMilestone[];
}

export default function DependencyDialog({
  open,
  onClose,
  sourceKind,
  sourceId,
  sourceName,
  availableItems,
  milestones,
}: DependencyDialogProps) {
  const qc = useQueryClient();
  const [value, setValue] = useState<DependencyRef[]>([]);
  const [nameInput, setNameInput] = useState(sourceName);

  useEffect(() => {
    setNameInput(sourceName);
  }, [sourceName, open]);

  const table = sourceKind === 'item' ? 'items' : 'item_milestones';

  const { data, isLoading } = useQuery({
    queryKey: ['bar-depends-on', sourceKind, sourceId],
    enabled: open && !!sourceId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from(table)
        .select('depends_on')
        .eq('id', sourceId)
        .maybeSingle();
      if (error) throw error;
      const raw = (data as any)?.depends_on;
      if (!Array.isArray(raw)) return [] as DependencyRef[];
      const out: DependencyRef[] = [];
      for (const r of raw) {
        if (!r || typeof r !== 'object') continue;
        const kind = r.kind === 'milestone' ? 'milestone' : r.kind === 'item' ? 'item' : null;
        const id = typeof r.id === 'string' ? r.id : null;
        if (!kind || !id) continue;
        const type = r.type === 'SS' || r.type === 'FF' ? r.type : 'FS';
        const lagN = typeof r.lag === 'number' ? r.lag : parseInt(r.lag, 10);
        const lag = Number.isFinite(lagN) ? lagN : 0;
        out.push({ kind, id, type, lag });
      }
      return out;
    },
  });

  useEffect(() => {
    if (open) setValue(Array.isArray(data) ? data : []);
  }, [open, data]);

  const saveMutation = useMutation({
    mutationFn: async (next: DependencyRef[]) => {
      const trimmedName = nameInput.trim();
      const nameChanged = trimmedName.length > 0 && trimmedName !== sourceName.trim();
      const payload: any = { depends_on: next };
      if (nameChanged) payload.name = trimmedName;
      const { error } = await supabase
        .from(table)
        .update(payload)
        .eq('id', sourceId);
      if (error) throw error;
      return { nameChanged };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['bar-depends-on', sourceKind, sourceId] });
      // Invalidate connector overlay consumers so dependency edges redraw immediately
      qc.invalidateQueries({ queryKey: ['items-depends-on'] });
      qc.invalidateQueries({ queryKey: ['milestones-depends-on'] });
      if (sourceKind === 'item') {
        qc.invalidateQueries({ queryKey: ['item-depends-on', sourceId] });
        qc.invalidateQueries({ queryKey: ['items'] });
      } else {
        qc.invalidateQueries({ queryKey: ['item-milestones'] });
      }
      if (result?.nameChanged) {
        qc.invalidateQueries({ queryKey: ['items-optimized'] });
        qc.invalidateQueries({ queryKey: ['items-milestones'] });
        qc.invalidateQueries({ queryKey: ['item-milestones'] });
        qc.invalidateQueries({ queryKey: ['project-items'] });
        qc.invalidateQueries({ queryKey: ['project-items-by-category'] });
      }
      toast.success('Đã lưu phụ thuộc');
      onClose();
    },
    onError: (e: any) => {
      toast.error(e?.message || 'Lưu phụ thuộc thất bại');
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>
            {(sourceKind === 'item' ? 'Hạng mục' : 'Mốc')} "{sourceName}" phụ thuộc vào:
          </DialogTitle>
          <DialogDescription>
            Chọn các hạng mục / mốc mà bar này phụ thuộc. Bỏ tick để gỡ.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="dep-dialog-name">Tên</Label>
          <Input
            id="dep-dialog-name"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Đang tải...</div>
        ) : (
          <DependencyPicker
            availableItems={availableItems}
            milestones={milestones}
            value={value}
            onChange={setValue}
            excludeKind={sourceKind}
            excludeId={sourceId}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saveMutation.isPending}>
            Hủy
          </Button>
          <Button onClick={() => saveMutation.mutate(value)} disabled={saveMutation.isPending || isLoading}>
            {saveMutation.isPending ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
